import { BlockComponentFactory } from 'shared/express-interface/blocks-core/BlockFactory'
import { AttributeDefinitionType, Conversationalv2TurnDataType, TaskMode } from 'Types/Tasks.types'
import { useCallback, useMemo, useState } from 'react'
import { ModelContent } from 'Tasks/ConversationTaskv2/UI/ModelContent/ModelContent'
import { UserContent } from 'Tasks/ConversationTaskv2/UI/UserContent/UserContent'
import Flex from 'antd/lib/flex'
import AIGenerateIcon from 'Assets/icons/generate.svg?react'
import { TaskBlock } from 'shared/express-interface/blocks-core/TaskBlock'
import { EditorContainer } from 'Tasks/ConversationTaskv2/UI/EditorContainer/EditorContainer'
import { ViolationType } from 'shared/violations/types'
import Icon from '@ant-design/icons/lib/components/Icon'
import { FileToModelValueType } from 'Types/Attribute.types'
import { useTaskContext } from 'Contexts/TaskContext'

export type ConversationsTurnBlockProps = {
    inputEditorBlockElement: TaskBlock<Record<string, unknown>>
    turnData: () => Conversationalv2TurnDataType[]
    userMessage: () => string
    editIndex: () => number
    setEditIndex: (index: number) => void
    isEditorStep: boolean
    isLoading: () => boolean
    isResponseStreaming: () => boolean
    streamingPath: () => string | null
    turnsCountFromLastResponse: number
    onClickRegenerateContent: () => void
    isRetryUnavailable: () => boolean
    mode: TaskMode
    setFilesList: (index: number, files: FileToModelValueType[]) => void
    submitEditTurn: (index: number) => void
    errors: () => ViolationType[]
    errorMessage: () => string | null
}

export const ConversationsTurnBlockComponent =
    BlockComponentFactory.createBlockComponent<ConversationsTurnBlockProps>(props => {
        const {
            inputEditorBlockElement,
            turnData: turnDataFn,
            userMessage: userMessageFn,
            editIndex: editIndexFn,
            setEditIndex,
            isEditorStep,
            isLoading: isLoadingFn,
            isResponseStreaming: isResponseStreamingFn,
            streamingPath: streamingPathFn,
            turnsCountFromLastResponse,
            onClickRegenerateContent,
            isRetryUnavailable: isRetryUnavailableFn,
            mode,
            setFilesList,
            submitEditTurn,
            errors: errorsFn,
            errorMessage: errorMessageFn,
        } = props

        const isResponseStreaming = isResponseStreamingFn()
        const streamingPath = streamingPathFn()
        const isLoading = isLoadingFn()
        const isRetryUnavailable = isRetryUnavailableFn()
        const editIndex = editIndexFn()
        const turnData = turnDataFn()
        const userMessage = userMessageFn()
        const errors = errorsFn()
        const errorMessage = errorMessageFn()

        const taskContext = useTaskContext()
        const taskId = taskContext.taskId
        const isReadOnly = taskContext.isReadOnly() && mode !== TaskMode.Triager

        const [isFileUploading, setIsFileUploading] = useState(false)

        const attributeGroups = taskContext
            .getAttributeManager?.()
            ?.getAttributeGroupsByDefinitionType(AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES)

        const getAttributeFilledPercentageByPath = useCallback(
            (path: string) => {
                if (!attributeGroups) {
                    return null
                }

                const groupsInPath =
                    attributeGroups?.filter(group => group.groupKey?.includes(path)) ?? []

                if (groupsInPath.length === 0) {
                    return null
                }

                const groupsFilledCount = groupsInPath.filter(
                    group => group.areMandatoryAttributesFilled
                )

                return { percent: (groupsFilledCount.length / groupsInPath.length) * 100 }
            },
            [attributeGroups]
        )

        const submitButtonDisabledReason = useMemo(() => {
            if (isLoading) {
                return 'Please wait while we are processing your request'
            }

            if (isFileUploading) {
                return 'Please wait while we are uploading your file'
            }

            if (userMessage?.trim() === '') {
                return 'Input is required'
            }

            const filteredErrors = errors.filter(
                error =>
                    error.id !== 'attributes' &&
                    !error.id.includes('turn-json-attributes') &&
                    !error.id.includes('live_feedback')
            )
            if (filteredErrors.length > 0) {
                return filteredErrors[0].message
            }

            return ''
        }, [isLoading, isFileUploading, userMessage, errors])

        const currentTurnDataElement = useMemo(() => {
            return (
                <>
                    {turnData.map((turn: Conversationalv2TurnDataType, index: number) => (
                        <div className="conversations-turn" key={index}>
                            <div className={'conversation-v2-user-turn'}>
                                <UserContent
                                    prompt={turn.prompt}
                                    onClickEditContent={() => setEditIndex(index)}
                                    isEditAllowed={
                                        !!isEditorStep &&
                                        !isLoading &&
                                        !isResponseStreaming &&
                                        index >= turnsCountFromLastResponse
                                    }
                                    urls={turn.media_files.map(file => file.path)}
                                />

                                {editIndex === index && (
                                    <div className="conversations-turn-edit-container">
                                        <EditorContainer
                                            inputEditorBlockElement={inputEditorBlockElement}
                                            filesList={turn.media_files}
                                            setFilesList={files => setFilesList(index, files)}
                                            buttonDisabledReason={submitButtonDisabledReason}
                                            submitTurn={() => submitEditTurn(index)}
                                            setEditIndex={setEditIndex}
                                            editIndex={editIndex}
                                            taskId={taskId}
                                            isVisible={editIndex === index}
                                            setIsFileUploading={setIsFileUploading}
                                            isReadOnly={isReadOnly}
                                        />
                                    </div>
                                )}
                            </div>

                            {turn.error ? (
                                <Flex
                                    gap={10}
                                    className="model-content-root model-content-error-container"
                                    justify="flex-start"
                                    align="flex-start"
                                >
                                    <Icon
                                        className="model-content-generate-icon"
                                        component={AIGenerateIcon}
                                    />
                                    <div className="model-content-error-text">{turn.error}</div>
                                </Flex>
                            ) : (
                                <ModelContent
                                    path={'$'}
                                    mode={mode}
                                    data={turn.modelResponse ?? {}}
                                    title={''}
                                    nesting={1}
                                    isReadOnly={isReadOnly}
                                    getAttributeFilledPercentageByPath={
                                        getAttributeFilledPercentageByPath
                                    }
                                    turnIndex={index + 1}
                                    isLoading={isLoading}
                                    isResponseStreaming={isResponseStreaming}
                                    streamingPath={streamingPath}
                                    isLatestTurn={index === turnData.length - 1}
                                    onClickRegenerateContent={onClickRegenerateContent}
                                    isRegenerationAllowed={
                                        index === turnData.length - 1 &&
                                        !!isEditorStep &&
                                        !isRetryUnavailable
                                    }
                                    errorMessage={
                                        index === turnData.length - 1 ? errorMessage : null
                                    }
                                />
                            )}
                        </div>
                    ))}
                </>
            )
        }, [
            editIndex,
            getAttributeFilledPercentageByPath,
            inputEditorBlockElement,
            isEditorStep,
            isLoading,
            isReadOnly,
            isRetryUnavailable,
            mode,
            onClickRegenerateContent,
            setEditIndex,
            setFilesList,
            submitButtonDisabledReason,
            submitEditTurn,
            taskId,
            turnData,
            turnsCountFromLastResponse,
            errorMessage,
            streamingPath,
            isResponseStreaming,
        ])

        return <div className="conversations-v2-turns-container">{currentTurnDataElement}</div>
    })
